import CustomTooltip from '_components/CustomTooltip';
import ErrorBoundary from '_components/error/ErrorBoundary';
import { ConnectedBusIndicator } from '_components/indicators';
import { alertActions, vehicleActions } from '_store';
import { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, Tooltip, ZoomControl, useMap } from 'react-leaflet'
import { useDispatch, useSelector } from 'react-redux';
import { MANUFACTURING_SITE as site }  from 'types/globals';
import L from 'leaflet';

export { GeolocalisationMap };

/**
 * Carte de géolocalisation d'un bus
 * Récupère l'ensemble des bus au chargement
 * @returns 
 */
function GeolocalisationMap() {
    const dispatch = useDispatch()
    const [ location, setLocation ] = useState([site.coord.lat, site.coord.lng])
    const [ nbVehicles, setNbVehicles ] = useState(0);

    const vehicle = useSelector(state => state.vehicles.item);
    const vehicles = useSelector(state => state.vehicles.list);
    const showAllGeoloc = useSelector(state => state.vehicles.showAllGeoloc);

    /* Gestion du centrage de la carte en fonction des résultats affichés */
    const mapRef = useRef(null);

    // Ajuste la carte en fonction des éléments
    const fitMapBounds = () => {
        const map = mapRef.current;

        // Détermine les coordonnées en fonction de showAllGeoloc
        const bounds = (showAllGeoloc && vehicles) ? 
                        vehicles.value?.map(vehicle => L.latLng(vehicle.lat, vehicle.lng)) :
                        L.latLng(location[0], location[1]);
        
        if (bounds?.length > 0 && map) {
            map.fitBounds(bounds);
        }
    };

    // Ajuste les bounds lorsque showAllGeoloc ou les données changent
    useEffect(() => {
        fitMapBounds();
    }, [showAllGeoloc, vehicles, location]);

    /* Hook d'effet appelé à l'initialisation du composant */
    useEffect(() => {
        async function getData() {
            dispatch(alertActions.clear());
            try {
                // get Vehicles data from server
                let message;
                const response = await dispatch(vehicleActions.getAll());
                if (response?.error){
                    dispatch(alertActions.error(response.error?.message));
                } else {
                    message = 'Liste de véhicule mise à jour';
                    dispatch(alertActions.success({ message, showAfterRedirect: true }));
                }                
            } catch (error) {
                dispatch(alertActions.error(error));
            }
        }
        getData();
        const interval = setInterval(() => {
            getData();
        }, 120000);
        return () => clearInterval(interval);        
    }, []);

    /* Hook d'effet lorsqu'un véhicule est sélectionné ou désélectionné
    La carte est centrée soit sur le bus sélectionné soit sur l'usine */
    useEffect(() => {
        vehicle ? setLocation([vehicle.lat, vehicle.lng]) : setLocation([site.coord.lat, site.coord.lng]); 
    }, [vehicle])  
    
    useEffect(() => {
        if(vehicles?.value?.length){
            setNbVehicles(vehicles.value.length/2);
        }
    }, [vehicles])

    /**
     * Permet de recentrer automatiquement la carte
     * @param {*} param0 
     * @returns 
     */
    const RecenterAutomatically = ({lat,lng}) => {
        const map = useMap();
        useEffect(() => {
            map.setView([lat, lng]);
        }, [lat, lng]);
        return null;
    }

    /**
     * Tooltip personnalisé pour l'affichage des informations d'un bus
     * @returns 
     */
    function TooltipGeolocalisation({vehicle}) {
        const props = {
            title: "Informations Bus",
            bg: "bg-[#6F80FF]",
            data: [
                {
                    title: "Contremarque",
                    value: vehicle?.contremarque,
                },
                {
                    title: "Numéro VIN / Production",
                    value: vehicle?.number,
                },
                {
                    title: "Dernière mise à jour",
                    value: vehicle?.lastUpdate,
                }
            ],
        };
        return (
            <CustomTooltip
                key={vehicle?.number}
                data={props.data}
                bg={props.bg}
                title={props.title}
                titleValue={props.titleValue}
            />
        );
    };

    return (        
        <ErrorBoundary>            
            <MapContainer 
                center={location} 
                zoom={16} 
                minZoom={11}
                scrollWheelZoom={'center'}
                zoomControl={false}
                ref={mapRef}
                >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {!showAllGeoloc && vehicle && 
                    <Marker position={location} key={vehicle.lat + vehicle.lng}>
                        {vehicle && <Tooltip className='custom-tooltip'><TooltipGeolocalisation key={location[0]} vehicle={vehicle} /></Tooltip>}
                    </Marker>
                }
                {showAllGeoloc && vehicles?.value && vehicles.value.map((vehicle, index) => (
                    <Marker position={[vehicle.lat, vehicle.lng]} key={index + vehicle.lat + vehicle.lng}>
                        {vehicle && <Tooltip className='custom-tooltip'><TooltipGeolocalisation key={index} vehicle={vehicle}/></Tooltip>}
                    </Marker>
                ))}
        
                <ZoomControl position="bottomright" />
                <RecenterAutomatically lat={location[0]} lng={location[1]}/>       
                <ConnectedBusIndicator value={nbVehicles} />
            </MapContainer>
        </ErrorBoundary>
    )
}
